import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Activity, Bot, Database, ExternalLink, Gauge, RefreshCw, Sparkles, Zap } from 'lucide-react'
import apiClient from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { AIDevEnv, AIDevSource, AIDevStatsResponse, AIDevTurn, AIDevWindow } from '@/types/ai'
import { LANE_LABELS, formatAgo, formatMs, formatPct, formatUsd, sortedCounts } from './aiDevFormat'
import { QualityPanel } from './QualityPanel'

// The AI developer dashboard: how the Bhookly AI copilot is doing across every
// restaurant — turns, speed, cost, cache hits, Jev's decisions, misses — read
// from LangSmith by the AI platform (or from the platform's own live log).
// Shown as a tab of the admin dashboard only where AI_DEV_DASHBOARD=1 and only
// to admins (useAIDevDashboardGate); a restaurant's own deploy never shows it.

const WINDOWS: { value: AIDevWindow; label: string }[] = [
  { value: '1h', label: '1 h' },
  { value: '24h', label: '24 h' },
  { value: '7d', label: '7 days' },
  { value: 'all', label: 'All' },
]
const ENVS: AIDevEnv[] = ['local', 'development', 'staging', 'production']

const LANE_COLORS: Record<string, string> = {
  jev_fast: 'bg-teal-500',
  jev_tool: 'bg-emerald-500',
  base: 'bg-blue-500',
  cache: 'bg-violet-500',
  offline: 'bg-muted-foreground',
  upper: 'bg-muted-foreground',
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'px-3 py-1.5 text-xs font-medium transition-colors',
            value === o.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function Kpi({ title, value, sub, icon }: { title: string; value: string; sub?: React.ReactNode; icon: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {icon}
          {title}
        </div>
        <div className="mt-1.5 text-2xl font-semibold tabular-nums">{value}</div>
        {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  )
}

function Bars({ rows, color = 'bg-primary' }: { rows: [string, number][]; color?: string }) {
  if (!rows.length) return <div className="py-3 text-sm text-muted-foreground">Nothing yet.</div>
  const max = Math.max(...rows.map((r) => r[1]))
  return (
    <div className="space-y-1.5">
      {rows.map(([k, v]) => (
        <div key={k} className="grid grid-cols-[minmax(0,40%)_1fr_3rem] items-center gap-2 text-sm">
          <span className="truncate" title={k}>
            {k}
          </span>
          <div className="h-2 rounded bg-muted">
            <div className={cn('h-2 rounded', color)} style={{ width: `${(100 * v) / max}%` }} />
          </div>
          <span className="text-right tabular-nums text-muted-foreground">{v}</span>
        </div>
      ))}
    </div>
  )
}

function HourlyChart({ hourly }: { hourly: AIDevStatsResponse['stats']['hourly'] }) {
  const max = Math.max(1, ...hourly.map((h) => h.turns))
  return (
    <div>
      <div className="flex h-36 items-end gap-[3px]">
        {hourly.map((h) => {
          const time = new Date(h.hour * 1000).toLocaleTimeString([], { hour: '2-digit' })
          return (
            <div
              key={h.hour}
              className="relative flex-1 rounded-sm bg-primary/70"
              style={{ height: `${(100 * h.turns) / max}%`, minHeight: h.turns ? 2 : 0 }}
              title={`${time}: ${h.turns} turns, ${h.cache_hits} from cache, ${formatUsd(h.cost)}`}
            >
              <div
                className="absolute inset-x-0 bottom-0 rounded-sm bg-violet-500"
                style={{ height: h.turns ? `${(100 * h.cache_hits) / h.turns}%` : 0 }}
              />
            </div>
          )
        })}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
        <span>{hourly.length ? new Date(hourly[0].hour * 1000).toLocaleTimeString([], { hour: '2-digit' }) : ''}</span>
        <span>now</span>
      </div>
      <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm bg-primary/70" />
          turns
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm bg-violet-500" />
          answered from cache
        </span>
      </div>
    </div>
  )
}

function TurnRow({ t, open, onToggle }: { t: AIDevTurn; open: boolean; onToggle: () => void }) {
  const jev = t.jev ?? {}
  return (
    <Fragment>
      <tr className="cursor-pointer border-b hover:bg-muted/50" onClick={onToggle}>
        <td className="whitespace-nowrap px-2 py-1.5 text-muted-foreground" title={new Date(t.ts * 1000).toLocaleString()}>
          {formatAgo(t.ts)}
        </td>
        <td className="px-2 py-1.5">
          {t.tenant} <span className="text-muted-foreground">· {t.role}</span>
        </td>
        <td className="px-2 py-1.5">
          {t.status !== 'ok' ? (
            <Badge variant="destructive">{t.status}</Badge>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span className={cn('h-2 w-2 rounded-full', LANE_COLORS[t.lane ?? ''] ?? 'bg-muted-foreground')} />
              {t.lane}
              {t.cache && (
                <Badge variant="secondary" className="text-[10px]">
                  cache {t.cache.how}
                </Badge>
              )}
            </span>
          )}
        </td>
        <td className="px-2 py-1.5 text-right tabular-nums">{formatMs(t.latency_ms)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums">{formatUsd(t.cost_usd ?? 0, 5)}</td>
        <td className="px-2 py-1.5">
          {jev.error ? (
            <Badge variant="destructive">{jev.error}</Badge>
          ) : jev.intent ? (
            <span>
              {jev.intent}
              {jev.tool ? ` → ${jev.tool}` : ''}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-2 py-1.5">
          <div className="flex flex-wrap gap-1">
            {(t.tools ?? []).map((tool, i) => (
              <Badge key={i} variant={tool.status === 'ok' || tool.status === 'cached' ? 'outline' : 'destructive'}>
                {tool.name}
              </Badge>
            ))}
            {t.opened && <Badge variant="outline">→ {t.opened}</Badge>}
            {(t.misses ?? []).map((m) => (
              <Badge key={m} variant="destructive">
                {m}
              </Badge>
            ))}
          </div>
        </td>
        <td className="px-2 py-1.5 text-right">
          {t.trace_url && (
            <a
              href={t.trace_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              trace <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b bg-muted/30">
          <td colSpan={8} className="space-y-1 px-3 py-2 text-xs">
            {t.message && (
              <div>
                <span className="font-semibold">Asked:</span> {t.message}
              </div>
            )}
            {t.reply && (
              <div>
                <span className="font-semibold">Replied:</span> {t.reply}
              </div>
            )}
            <div>
              <span className="font-semibold">Turn:</span> {t.turn} · <span className="font-semibold">Models:</span>{' '}
              {(t.models ?? []).join(', ') || '—'} · <span className="font-semibold">Tokens:</span> {t.prompt_tokens ?? 0} in (
              {t.cached_tokens ?? 0} cached) · {t.completion_tokens ?? 0} out
            </div>
            <div>
              <span className="font-semibold">Jev:</span> {JSON.stringify(t.jev ?? null)}
            </div>
            {t.cache && (
              <div>
                <span className="font-semibold">Cache:</span> {JSON.stringify(t.cache)}
              </div>
            )}
            <div>
              <span className="font-semibold">Help articles:</span> {(t.articles ?? []).join(', ') || '—'}
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  )
}

export function AIDevDashboard() {
  const [win, setWin] = useState<AIDevWindow>('24h')
  const [source, setSource] = useState<AIDevSource>('langsmith')
  const [env, setEnv] = useState<AIDevEnv | ''>('')
  const [tenant, setTenant] = useState('')
  const [auto, setAuto] = useState(true)
  const [openTurns, setOpenTurns] = useState<Set<string>>(new Set())

  const q = useQuery({
    queryKey: ['ai-dev-stats', win, source, env, tenant],
    queryFn: () =>
      apiClient.aiDevStats({ window: win, source, env: env || undefined, tenant: tenant || undefined }),
    refetchInterval: auto ? 30_000 : false,
    staleTime: 10_000,
  })
  const d = q.data?.data
  const s = d?.stats
  const p = d?.platform
  const ls = d?.langsmith

  const toggleTurn = (id: string) =>
    setOpenTurns((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  // The quality loop (F-AI-55) reads this backend, not the AI platform, so it
  // renders even when the stats cannot be loaded.
  if (q.isError) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            Could not load the AI stats. Check that the AI platform is running and AI_DEV_DASHBOARD is on.
          </CardContent>
        </Card>
        <QualityPanel />
      </div>
    )
  }

  const tenants = Array.from(new Set([...Object.keys(s?.per_tenant ?? {}), ...(p?.tenants ?? []), tenant].filter(Boolean)))

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <div className="mr-auto min-w-0">
            <div className="flex items-center gap-2 text-base font-semibold">
              <Bot className="h-5 w-5 text-primary" /> Bhookly AI copilot
              <Badge variant="secondary">developer view</Badge>
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {d
                ? d.source === 'langsmith'
                  ? `Every restaurant's turns from LangSmith · ${ls?.project ?? ''} · ${ls?.runs ?? 0} turns${ls?.capped ? ' (newest only)' : ''}`
                  : `This AI server's own log · up ${formatMs((p?.uptime_s ?? 0) * 1000)}`
                : 'loading…'}
              {ls?.error && <span className="ml-2 text-destructive">LangSmith unavailable: {ls.error}</span>}
            </div>
          </div>
          <Segmented
            value={source}
            onChange={setSource}
            options={[
              { value: 'langsmith', label: 'LangSmith · all restaurants' },
              { value: 'live', label: 'This server' },
            ]}
          />
          {source === 'langsmith' && (
            <select
              value={env || ls?.env || ''}
              onChange={(e) => setEnv(e.target.value as AIDevEnv)}
              className="h-8 rounded-md border bg-background px-2 text-xs"
              aria-label="Environment"
            >
              {ENVS.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          )}
          <select
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            className="h-8 rounded-md border bg-background px-2 text-xs"
            aria-label="Restaurant"
          >
            <option value="">All restaurants</option>
            {tenants.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <Segmented value={win} onChange={setWin} options={WINDOWS} />
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> auto-refresh
          </label>
          <Button size="sm" variant="outline" onClick={() => q.refetch()} disabled={q.isFetching}>
            <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', q.isFetching && 'animate-spin')} /> Refresh
          </Button>
          {ls?.url && (
            <Button size="sm" variant="outline" asChild>
              <a href={ls.url} target="_blank" rel="noreferrer">
                LangSmith <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </Button>
          )}
        </CardContent>
      </Card>

      <QualityPanel />

      {/* Live state */}
      {p && (
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline">
            LLM · {p.llm.model} ({p.llm.provider}, effort {p.llm.effort})
          </Badge>
          <Badge variant={p.jev.breaker_open ? 'destructive' : 'outline'}>
            Jev · {p.jev.mode} · {p.jev.model}
            {p.jev.breaker_open ? ` · breaker open ${p.jev.breaker_seconds_left}s` : ''}
          </Badge>
          <Badge variant="outline">
            Cache · {p.cache.enabled ? `on, ${p.cache.ttl_days} days` : 'off'} · match ≥ {p.cache.min_similarity} + Jev ≥{' '}
            {p.jev.thresholds.cache_same}
          </Badge>
          <Badge variant="outline">
            Help articles · {p.kb.articles} ({p.kb.kb_version}) · {p.kb.screens} screens
          </Badge>
          <Badge variant="outline">
            Pinecone · {p.pinecone.index} ·{' '}
            {Object.entries(p.pinecone.namespaces)
              .map(([k, v]) => `${k} ${v}`)
              .join(' · ') || '—'}
          </Badge>
          <Badge variant="outline">Tracing · {p.tracing}</Badge>
        </div>
      )}

      {/* Headline numbers */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {!s ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24" />)
        ) : (
          <>
            <Kpi
              title="Turns"
              icon={<Activity className="h-3.5 w-3.5" />}
              value={String(s.turns)}
              sub={
                <>
                  {s.ok} answered · <span className={s.timeouts ? 'text-destructive' : ''}>{s.timeouts} timed out</span> ·{' '}
                  <span className={s.failed ? 'text-destructive' : ''}>{s.failed} failed</span>
                </>
              }
            />
            <Kpi
              title="Response time"
              icon={<Gauge className="h-3.5 w-3.5" />}
              value={formatMs(s.latency.p50)}
              sub={`median · p90 ${formatMs(s.latency.p90)} · p99 ${formatMs(s.latency.p99)}`}
            />
            <Kpi
              title="Cost"
              icon={<Zap className="h-3.5 w-3.5" />}
              value={formatUsd(s.cost.total, 3)}
              sub={`${formatUsd(s.cost.per_turn, 5)} a turn · LLM ${formatUsd(s.cost.llm, 3)} · Jev ${formatUsd(s.cost.jev, 3)}`}
            />
            <Kpi
              title="From cache"
              icon={<Database className="h-3.5 w-3.5" />}
              value={formatPct(s.cache.hit_rate)}
              sub={`${s.cache.hits} of ${s.ok} · exact ${(s.cache.exact_answer ?? 0) + (s.cache.exact_plan ?? 0)} · by meaning ${s.cache.semantic_answer ?? 0}`}
            />
            <Kpi
              title="Jev handled"
              icon={<Sparkles className="h-3.5 w-3.5" />}
              value={formatPct(s.ok ? (s.jev.fast_navs + s.jev.tool_calls) / s.ok : 0)}
              sub={`${s.jev.fast_navs} screens opened · ${s.jev.tool_calls} data calls`}
            />
            <Kpi
              title="Tokens"
              icon={<Bot className="h-3.5 w-3.5" />}
              value={`${(s.tokens.prompt / 1000).toFixed(1)}k`}
              sub={`in · ${(s.tokens.cached / 1000).toFixed(1)}k cached · ${(s.tokens.completion / 1000).toFixed(1)}k out`}
            />
          </>
        )}
      </div>

      {s && (
        <>
          <div className="grid min-w-0 gap-6 lg:grid-cols-[3fr_2fr]">
            <Card className="min-w-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Restaurants</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="px-2 py-1.5 font-medium">Restaurant</th>
                      <th className="px-2 py-1.5 text-right font-medium">Turns</th>
                      <th className="px-2 py-1.5 text-right font-medium">Median</th>
                      <th className="px-2 py-1.5 text-right font-medium">Cost</th>
                      <th className="px-2 py-1.5 text-right font-medium">From cache</th>
                      <th className="px-2 py-1.5 text-right font-medium">Jev handled</th>
                      <th className="px-2 py-1.5 text-right font-medium">Misses</th>
                      <th className="px-2 py-1.5 text-right font-medium">Errors</th>
                      <th className="px-2 py-1.5 text-right font-medium">Last</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(s.per_tenant).map(([slug, t]) => (
                      <tr
                        key={slug}
                        className="cursor-pointer border-b hover:bg-muted/50"
                        onClick={() => setTenant(tenant === slug ? '' : slug)}
                        title="Click to show only this restaurant"
                      >
                        <td className="px-2 py-1.5 font-medium">{slug}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{t.turns}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{formatMs(t.p50_ms)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{formatUsd(t.cost, 4)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{t.cache_hits}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{t.jev_handled}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{t.misses}</td>
                        <td className={cn('px-2 py-1.5 text-right tabular-nums', t.errors && 'text-destructive')}>{t.errors}</td>
                        <td className="px-2 py-1.5 text-right text-muted-foreground">{t.last ? formatAgo(t.last) : '—'}</td>
                      </tr>
                    ))}
                    {!Object.keys(s.per_tenant).length && (
                      <tr>
                        <td colSpan={9} className="px-2 py-4 text-center text-muted-foreground">
                          No turns in this window.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
            <Card className="min-w-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Turns per hour · last 24 h</CardTitle>
              </CardHeader>
              <CardContent>
                <HourlyChart hourly={s.hourly} />
              </CardContent>
            </Card>
          </div>

          <div className="grid min-w-0 gap-6 lg:grid-cols-2">
            <Card className="min-w-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">How turns were answered</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="px-2 py-1.5 font-medium">Lane</th>
                      <th className="px-2 py-1.5 font-medium">Share</th>
                      <th className="px-2 py-1.5 text-right font-medium">Turns</th>
                      <th className="px-2 py-1.5 text-right font-medium">Median</th>
                      <th className="px-2 py-1.5 text-right font-medium">p90</th>
                      <th className="px-2 py-1.5 text-right font-medium">Cost / turn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(s.lanes).map(([lane, v]) => (
                      <tr key={lane} className="border-b">
                        <td className="px-2 py-1.5">
                          <span className="flex items-center gap-1.5">
                            <span className={cn('h-2 w-2 rounded-full', LANE_COLORS[lane] ?? 'bg-muted-foreground')} />
                            <span title={lane}>{LANE_LABELS[lane] ?? lane}</span>
                          </span>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="h-2 min-w-16 rounded bg-muted">
                            <div className={cn('h-2 rounded', LANE_COLORS[lane] ?? 'bg-muted-foreground')} style={{ width: formatPct(v.share) }} />
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{v.turns}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{formatMs(v.p50_ms)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{formatMs(v.p90_ms)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{formatUsd(v.cost_per_turn, 5)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
            <Card className="min-w-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Answer cache</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Bars
                  color="bg-violet-500"
                  rows={[
                    ['Exact repeat · answer', s.cache.exact_answer ?? 0],
                    ['Exact repeat · data decision', s.cache.exact_plan ?? 0],
                    ['Close match · Jev said same', s.cache.semantic_answer ?? 0],
                    ['Close match · Jev said different', s.cache.semantic_rejected ?? 0],
                  ]}
                />
                <div className="text-xs text-muted-foreground">
                  On this server since start: {d?.counters['cache.lookups'] ?? 0} lookups · {d?.counters['cache.read_failed'] ?? 0} failed reads · stored{' '}
                  {d?.counters['cache.writes.answer'] ?? 0} answers and {d?.counters['cache.writes.plan'] ?? 0} decisions ·{' '}
                  {d?.counters['cache.purged'] ?? 0} expired removed
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid min-w-0 gap-6 lg:grid-cols-3">
            <Card className="min-w-0 lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Jev</CardTitle>
                <div className="text-xs text-muted-foreground">
                  {s.jev.checked} checks · median {formatMs(s.jev.p50_ms)} · p90 {formatMs(s.jev.p90_ms)} · {(s.jev.tokens_per_check / 1000).toFixed(1)}k tokens
                  each
                  {Object.keys(s.jev.errors).length > 0 && (
                    <span className="ml-2 text-destructive">
                      errors: {sortedCounts(s.jev.errors).map(([k, v]) => `${k} ${v}`).join(' · ')}
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="grid gap-6 sm:grid-cols-2">
                <div>
                  <div className="mb-2 text-xs font-medium text-muted-foreground">Request type</div>
                  <Bars rows={sortedCounts(s.jev.intents)} color="bg-teal-500" />
                </div>
                <div>
                  <div className="mb-2 text-xs font-medium text-muted-foreground">Data call — made, or why not</div>
                  <Bars rows={sortedCounts(s.jev.tool_reasons)} color="bg-emerald-500" />
                </div>
              </CardContent>
            </Card>
            <div className="grid min-w-0 gap-6">
              <Card className="min-w-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Misses</CardTitle>
                </CardHeader>
                <CardContent>
                  <Bars rows={sortedCounts(s.misses)} color="bg-destructive" />
                </CardContent>
              </Card>
              <Card className="min-w-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Tools called</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  {Object.entries(s.tools).map(([name, v]) => (
                    <div key={name} className="flex justify-between gap-2">
                      <span className="truncate">{name}</span>
                      <span className="tabular-nums text-muted-foreground">
                        {v.calls} calls · {v.ok} ok · {formatMs(v.p50_ms)}
                      </span>
                    </div>
                  ))}
                  {!Object.keys(s.tools).length && <div className="text-muted-foreground">No tools called yet.</div>}
                </CardContent>
              </Card>
            </div>
          </div>

          <Card className="min-w-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Recent turns</CardTitle>
              <div className="text-xs text-muted-foreground">Click a row for details; “trace” opens the turn in LangSmith.</div>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="px-2 py-1.5 font-medium">When</th>
                    <th className="px-2 py-1.5 font-medium">Restaurant · role</th>
                    <th className="px-2 py-1.5 font-medium">Answered by</th>
                    <th className="px-2 py-1.5 text-right font-medium">Time</th>
                    <th className="px-2 py-1.5 text-right font-medium">Cost</th>
                    <th className="px-2 py-1.5 font-medium">Jev</th>
                    <th className="px-2 py-1.5 font-medium">Tools · screen · misses</th>
                    <th className="px-2 py-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {s.recent.map((t) => (
                    <TurnRow key={`${t.turn}-${t.ts}`} t={t} open={openTurns.has(t.turn)} onToggle={() => toggleTurn(t.turn)} />
                  ))}
                  {!s.recent.length && (
                    <tr>
                      <td colSpan={8} className="px-2 py-4 text-center text-muted-foreground">
                        No turns yet — ask the copilot something.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
